// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Auth, Hub } from 'aws-amplify';

const Navbar = () => {
    const [isAuth, setIsAuth] = useState(false);
    const [user, setUser] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    const checkAuth = async () => {
        try {
            const u = await Auth.currentAuthenticatedUser();
            setIsAuth(true);
            setUser({ name: u.attributes?.email?.split('@')[0] || u.username });
        } catch { setIsAuth(false); setUser(null); }
    };

    useEffect(() => {
        checkAuth();
        const l = ({ payload }) => {
            if (payload.event === 'signIn') checkAuth();
            if (payload.event === 'signOut') {
                setIsAuth(false); setUser(null);
                localStorage.removeItem('isAuthenticated');
                window.location.href = '/';
            }
        };
        Hub.listen('auth', l);
        return () => Hub.remove('auth', l);
    }, []);

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', fn);
        return () => window.removeEventListener('scroll', fn);
    }, []);

    useEffect(() => setMobileOpen(false), [location]);

    const isActive = p => location.pathname === p;
    const links = [
        { to: '/', label: 'Home' },
        { to: '/upload', label: 'Report' },
        { to: '/dashboard', label: 'Browse' },
        ...(isAuth ? [{ to: '/my-profile', label: 'My Items' }] : []),
    ];

    return (
        <>
            <header style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
                height: 60,
                background: scrolled ? 'rgba(242,239,232,0.9)' : 'transparent',
                backdropFilter: scrolled ? 'blur(16px)' : 'none',
                WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
                borderBottom: scrolled ? '1px solid rgba(26,23,20,0.08)' : '1px solid transparent',
                transition: 'all 0.25s ease',
            }}>
                <div style={{
                    maxWidth: 1160, margin: '0 auto', padding: '0 32px',
                    height: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    {/* Logo */}
                    <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                            width: 28, height: 28, background: 'var(--ink)',
                            borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <circle cx="6" cy="6" r="4" stroke="var(--cream)" strokeWidth="1.5"/>
                                <path d="M9.5 9.5L13 13" stroke="var(--cream)" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <span style={{
                            fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 15,
                            color: 'var(--ink)', letterSpacing: '-0.01em',
                        }}>lostit</span>
                    </Link>

                    {/* Center nav */}
                    <nav className="desk-nav" style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {links.map(({ to, label }) => (
                            <Link key={to} to={to} style={{
                                textDecoration: 'none', padding: '5px 14px', borderRadius: 99,
                                fontSize: 14, fontWeight: isActive(to) ? 500 : 400,
                                color: isActive(to) ? 'var(--ink)' : 'var(--ink-3)',
                                background: isActive(to) ? 'rgba(26,23,20,0.07)' : 'transparent',
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => !isActive(to) && (e.target.style.color = 'var(--ink)')}
                            onMouseLeave={e => !isActive(to) && (e.target.style.color = 'var(--ink-3)')}
                            >{label}</Link>
                        ))}
                    </nav>

                    {/* Right CTA */}
                    <div className="desk-nav" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {isAuth ? (
                            <>
                                <span style={{
                                    fontSize: 13, color: 'var(--ink-3)', fontWeight: 400,
                                    padding: '4px 10px',
                                }}>Hi, {user?.name}</span>
                                <button onClick={() => Auth.signOut()} className="btn btn-outline btn-sm">
                                    Sign out
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={() => Auth.federatedSignIn()}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        fontSize: 14, fontWeight: 400, color: 'var(--ink-2)',
                                        padding: '5px 12px', fontFamily: 'var(--sans)',
                                    }}>Log in</button>
                                <Link to="/upload" className="btn btn-ink btn-sm">Get started</Link>
                            </>
                        )}
                    </div>

                    {/* Mobile toggle */}
                    <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-btn"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'none' }}>
                        {mobileOpen ? (
                            <svg width="20" height="20" fill="none" stroke="var(--ink)" strokeWidth="1.5" viewBox="0 0 24 24">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        ) : (
                            <svg width="20" height="20" fill="none" stroke="var(--ink)" strokeWidth="1.5" viewBox="0 0 24 24">
                                <path d="M3 12h18M3 6h18M3 18h18"/>
                            </svg>
                        )}
                    </button>
                </div>
            </header>

            {/* Mobile menu */}
            {mobileOpen && (
                <div style={{
                    position: 'fixed', top: 60, left: 0, right: 0, zIndex: 99,
                    background: 'var(--cream)', borderBottom: '1px solid var(--border)',
                    padding: '12px 24px 20px',
                }}>
                    {links.map(({ to, label }) => (
                        <Link key={to} to={to} style={{
                            display: 'block', padding: '10px 0',
                            fontSize: 16, fontWeight: isActive(to) ? 500 : 400,
                            color: isActive(to) ? 'var(--ink)' : 'var(--ink-3)',
                            textDecoration: 'none',
                            borderBottom: '1px solid var(--border-2)',
                        }}>{label}</Link>
                    ))}
                    <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                        {isAuth
                            ? <button onClick={() => Auth.signOut()} className="btn btn-outline" style={{ flex: 1 }}>Sign out</button>
                            : <><button onClick={() => Auth.federatedSignIn()} className="btn btn-outline" style={{ flex: 1 }}>Log in</button>
                                <Link to="/upload" className="btn btn-ink" style={{ flex: 1 }}>Get started</Link></>
                        }
                    </div>
                </div>
            )}

            <style>{`
                @media (max-width: 720px) {
                    .desk-nav { display: none !important; }
                    .mob-btn  { display: flex !important; }
                }
            `}</style>
        </>
    );
};
export default Navbar;